import React, { useEffect, useState } from "react";
import api from "../../../api/axios";

import {
    DndContext,
    PointerSensor,
    useSensor,
    useSensors,
    closestCenter,
} from "@dnd-kit/core";

import {
    arrayMove,
    SortableContext,
    verticalListSortingStrategy,
    useSortable,
} from "@dnd-kit/sortable";

import { CSS } from "@dnd-kit/utilities";
import "./SubGroupDefaultSequence.css";

// ------------------------------------------------------
// DRAGGABLE ROW
// ------------------------------------------------------
const SortableRow = ({ id, children }) => {
    const { attributes, listeners, setNodeRef, transform, transition } =
        useSortable({ id });

    return (
        <div
            ref={setNodeRef}
            style={{
                transform: CSS.Transform.toString(transform),
                transition,
            }}
            className="sm-sortable-row"
        >
            <div className="sm-drag-handle" {...attributes} {...listeners}>
                ☰
            </div>
            <div className="sm-sortable-content">{children}</div>
        </div>
    );
};

const DEFAULT_SEQUENCE_KEY = "default";
const DEFAULT_SEQUENCE_NAME = "Default";

// ------------------------------------------------------
// MAIN COMPONENT
//
// section:    the section being edited (from the SectionManager draft)
// matchNames: SubGroup names the section matches (custom selection, catalog or own name)
// onSectionUpdated(updatedSection): called on "Apply"; the SectionManager keeps it as
//                                   a draft and saves it together with the scenario
// ------------------------------------------------------
const SubGroupDefaultSequence = ({
    isOpen,
    onClose,
    section,
    matchNames,
    onSectionUpdated,
}) => {
    const [editingOrder, setEditingOrder] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const sensors = useSensors(useSensor(PointerSensor));
    const namesKey = (matchNames || []).join("|");

    // ------------------------------------------------------
    // LOAD: types come from the SubGroups that currently match the section,
    // ordered by the saved default sequence (unknown / new types go last)
    // ------------------------------------------------------
    useEffect(() => {
        if (!isOpen || !section) return;

        let cancelled = false;

        const load = async () => {
            setLoading(true);
            setError("");
            try {
                const res = await api.post(
                    "/section/subgroups/match",
                    matchNames || []
                );

                const types = [
                    ...new Set((res.data || []).map((sg) => sg.type).filter(Boolean)),
                ];

                const seq = section.subGroupSequences?.find(
                    (s) => s.key === DEFAULT_SEQUENCE_KEY
                );
                const orderOf = (t) =>
                    seq?.groupOrders?.find((g) => g.type === t)?.order ?? 9999;

                types.sort((a, b) => orderOf(a) - orderOf(b)); // stable: unsaved types keep delivery order

                if (!cancelled) setEditingOrder(types);
            } catch (err) {
                console.error(err);
                if (!cancelled) setError("Failed to load sequence.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [isOpen, section?.id, namesKey]);

    // ------------------------------------------------------
    // DRAG END
    // ------------------------------------------------------
    const onDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) return;

        setEditingOrder((prev) =>
            arrayMove(prev, prev.indexOf(active.id), prev.indexOf(over.id))
        );
    };

    // ------------------------------------------------------
    // APPLY (kept in the draft, saved with the scenario)
    // ------------------------------------------------------
    const apply = () => {
        const groupOrders = editingOrder.map((type, i) => ({
            type,
            order: i + 1,
        }));

        const existing = (section.subGroupSequences || []).find(
            (s) => s.key === DEFAULT_SEQUENCE_KEY
        );
        const others = (section.subGroupSequences || []).filter(
            (s) => s.key !== DEFAULT_SEQUENCE_KEY
        );

        const defaultSeq = {
            ...(existing || {}),
            key: DEFAULT_SEQUENCE_KEY,
            name: DEFAULT_SEQUENCE_NAME,
            isDefault: true,
            groupOrders,
        };

        onSectionUpdated({
            ...section,
            subGroupSequences: [defaultSeq, ...others],
            activeSubGroupSequenceKey:
                section.activeSubGroupSequenceKey || DEFAULT_SEQUENCE_KEY,
        });
        onClose();
    };

    if (!isOpen) return null;

    // ------------------------------------------------------
    // RENDER
    // ------------------------------------------------------
    return (
        <div className="sm-overlay">
            <div className="sm-modal">
                <div className="sm-header">
                    <h2 className="sm-title">
                        Device Placement Sequence{section?.name ? `: ${section.name}` : ""}
                    </h2>
                    {error && <div className="sm-error">{error}</div>}
                </div>

                <div className="sm-body">
                    {loading && <div className="sm-status">Loading...</div>}

                    {!loading && !error && editingOrder.length === 0 && (
                        <div className="sm-status">
                            No subgroups match this section yet.
                        </div>
                    )}

                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={onDragEnd}
                    >
                        <SortableContext
                            items={editingOrder}
                            strategy={verticalListSortingStrategy}
                        >
                            {editingOrder.map((type) => (
                                <SortableRow key={type} id={type}>
                                    {type}
                                </SortableRow>
                            ))}
                        </SortableContext>
                    </DndContext>
                </div>

                <div className="sm-footer">
                    <button
                        className="btn-primary"
                        onClick={apply}
                        disabled={loading || !!error}
                    >
                        Apply
                    </button>
                    <button className="btn-secondary2" onClick={onClose}>
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SubGroupDefaultSequence;